// Cross-tab "Add <item> to this SOW" flow, shared by the Deliverable and
// Milestone tabs: when the URL carries `?action=create&scope=sow:<id>`,
// open the tab's create modal with the SOW pre-selected, then strip ONLY
// `action` so the user lands in SOW view after closing (`scope=sow:<id>`
// survives the navigation).
//
// Split into a Pattern 0 render-time guard (fires `onTrigger` exactly once
// per distinct action=create signal — no `react-hooks/set-state-in-effect`
// risk, since nothing here runs inside a useEffect) plus a separate,
// setState-free effect that strips the URL param (setSearchParams is a
// real router-mutation side effect that must stay in an effect).
//
// Validation is gated on the SOW list query having settled (`sowsLoaded`).
// Without the gate, a legitimate sowId would be treated as unresolvable on
// the first render before the SOW list loads.

import { useEffect, useState } from "react";
import type { SetURLSearchParams } from "react-router";

export interface UseSowScopedCreateActionOptions {
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
  /** The candidate SOW id from the tab's `?scope=sow:<id>` URL state, or
   *  null when the current scope isn't SOW-scoped. */
  scopeSowId: string | null;
  /** True once the SOW list query has settled (data !== undefined). */
  sowsLoaded: boolean;
  /** The currently-loaded SOWs — only their `id` is used, to resolve
   *  whether `scopeSowId` is real. */
  sows: readonly { id: string }[];
  /** Fired exactly once per distinct action=create signal, but only when
   *  `scopeSowId` resolves to a real SOW. Callers open their create modal
   *  (pre-filled with this id) from here. An unresolvable sowId still
   *  strips `action` from the URL but never fires this callback — matches
   *  the "just strip and skip the pre-fill" behaviour of the original
   *  per-tab implementations. */
  onTrigger: (sowId: string) => void;
}

export function useSowScopedCreateAction({
  searchParams,
  setSearchParams,
  scopeSowId,
  sowsLoaded,
  sows,
  onTrigger,
}: UseSowScopedCreateActionOptions): void {
  const isCreateSignal = searchParams.get("action") === "create" && sowsLoaded;
  // Pattern 0: fire onTrigger exactly once per distinct signal, keyed by
  // the scope's sowId at the time the signal appeared — a sentinel string
  // distinguishes "signal present but no SOW scoped" from "no signal" so
  // re-arriving at the same unresolvable signal doesn't re-fire either.
  const signalKey = isCreateSignal ? (scopeSowId ?? "__no-sow__") : null;
  const [handledSignal, setHandledSignal] = useState<string | null>(null);
  if (signalKey !== null && signalKey !== handledSignal) {
    setHandledSignal(signalKey);
    if (scopeSowId && sows.some((s) => s.id === scopeSowId)) {
      onTrigger(scopeSowId);
    }
  }

  useEffect(() => {
    if (searchParams.get("action") !== "create") return;
    if (!sowsLoaded) return;
    const next = new URLSearchParams(searchParams);
    next.delete("action");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, sowsLoaded]);
}
